import React, { useState } from "react";
import {
    Box,
    Link,
    useTheme as useMuiTheme,
    useMediaQuery,
    Typography,
    Divider,
    Stack,
} from "@mui/material";
import { useTheme, ThemeName } from "../util/themes";
import { getConfiguredThemeName } from "../util/themes/ThemeProvider";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import {
    IMAGE_BRIGHTNESS,
    TEXTS,
    HOVER_ANIMATION_DURATION,
} from "./CategoryShowcase/CategoryShowcaseConstants";
import {
    imageStyles,
    subtitleWrapperStyles,
    subtitleTextStyles,
} from "./CategoryShowcase/CategoryShowcaseStyles";

type DiscountTier = {
    percent: string;     // "10% OFF"
    threshold: string;   // "$600.000"
};

type BaseSlide = {
    autoplayDelayMs?: number;
};

type ImageSlide = BaseSlide & {
    type?: "image";
    image: string;
    title: string;
    link: string;
};

type BannerSlide = BaseSlide & {
    type: "banner";
    title: string;               // "MAYORISTA DE JOYAS Y ACCESORIOS"
    minPurchase: string;         // "$150.000"
    discountsTitle?: string;     // "DESCUENTOS"
    discounts: DiscountTier[];
    ctaText: string;             // "Shop online"
    locationText: string;        // "Local: Uriburu 384 - CABA"
};

type CarouselSlide = ImageSlide | BannerSlide;

interface HeroCarouselProps {
    slides: CarouselSlide[];
}

export default function HeroCarousel({ slides }: HeroCarouselProps) {
    const muiTheme = useMuiTheme();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(muiTheme.breakpoints.down(900));
    const isDarkTheme = getConfiguredThemeName() === ThemeName.DARK;
    const pick = <T,>(light: T, dark: T) => (isDarkTheme ? dark : light);

    const bannerBackgroundColor = pick("#F3EBDD", theme.colors.background.default);
    const bannerBackgroundImage = pick(
        "linear-gradient(135deg, #F7F1E8 0%, #EFE3D3 45%, #F7F1E8 100%)",
        `linear-gradient(135deg, ${theme.colors.background.paper} 0%, ${theme.colors.background.default} 45%, ${theme.colors.background.paper} 100%)`
    );

    const getImagePath = (imageName: string) => {
        const dotIndex = imageName.lastIndexOf(".");
        const nameWithoutExtension = imageName.substring(0, dotIndex);
        const extension = imageName.substring(dotIndex);

        const basePath = "images/carousel-1";
        const smallSuffix = isSmallScreen ? "_small" : "";

        return `${basePath}/${nameWithoutExtension}${smallSuffix}${extension}`;
    };

    const BannerSlideItem: React.FC<{ slide: BannerSlide }> = ({ slide }) => {
        return (
            <Box
                sx={{
                    position: "relative",
                    overflow: "hidden",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: { xs: 2, sm: 3 },
                    backgroundColor: bannerBackgroundColor,
                    backgroundImage: bannerBackgroundImage,
                }}
            >

                <Box
                    sx={{
                        width: "min(980px, 100%)",
                        textAlign: "center",
                        px: { xs: 2.5, sm: 4, md: 6 },
                        py: { xs: 3, sm: 4, md: 5 },
                        bgcolor: pick("rgba(255,255,255,0.72)", theme.colors.background.paper),
                        border: pick("1px solid rgba(0,0,0,0.08)", `1px solid ${theme.colors.greyLight}`),
                        borderRadius: { xs: 2, sm: 3 },
                        boxShadow: pick("0 18px 50px rgba(0,0,0,0.10)", "0 18px 50px rgba(0,0,0,0.30)"),
                        backdropFilter: "blur(10px)",
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: '"Rubik", sans-serif',
                            fontWeight: 800,
                            textTransform: "uppercase",
                            letterSpacing: { xs: "0.06em", md: "0.10em" },
                            color: pick("#111", theme.colors.text.primary),
                            lineHeight: 1.1,
                            fontSize: {
                                xs: "1.05rem",
                                sm: "1.35rem",
                                md: "2.05rem",
                                lg: "2.25rem",
                            },
                        }}
                    >
                        {slide.title}
                    </Typography>

                    <Box
                        sx={{
                            mt: { xs: 1.5, sm: 2 },
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "baseline",
                            gap: 1,
                            flexWrap: "wrap",
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: '"Rubik", sans-serif',
                                color: pick("rgba(0,0,0,0.70)", theme.colors.text.secondary),
                                fontWeight: 500,
                                fontSize: { xs: "0.95rem", sm: "1.05rem", md: "1.1rem" },
                            }}
                        >
                            Compra mínima
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: '"Rubik", sans-serif',
                                fontWeight: 800,
                                color: pick("#111", theme.colors.text.primary),
                                fontSize: { xs: "1.05rem", sm: "1.15rem", md: "1.25rem" },
                            }}
                        >
                            {slide.minPurchase}
                        </Typography>
                    </Box>

                    <Divider
                        sx={{
                            my: { xs: 2.25, sm: 2.75, md: 3.25 },
                            borderColor: pick("rgba(0,0,0,0.12)", theme.colors.greyLight),
                        }}
                    />

                    <Typography
                        sx={{
                            fontFamily: '"Rubik", sans-serif',
                            fontWeight: 800,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            color: pick("#111", theme.colors.text.primary),
                            fontSize: { xs: "0.95rem", sm: "1.05rem", md: "1.15rem" },
                        }}
                    >
                        {slide.discountsTitle ?? "DESCUENTOS"}
                    </Typography>

                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={{ xs: 1.5, md: 3 }}
                        sx={{
                            mt: { xs: 1.75, sm: 2.25 },
                            justifyContent: "center",
                            alignItems: "stretch",
                        }}
                    >
                        {slide.discounts.map((d, idx) => (
                            <Box
                                key={idx}
                                sx={{
                                    px: { xs: 2, sm: 2.5 },
                                    py: { xs: 1.5, sm: 2 },
                                    borderRadius: 2,
                                    bgcolor: pick("rgba(255,255,255,0.86)", theme.colors.greyExtraDark),
                                    border: pick("1px solid rgba(0,0,0,0.08)", `1px solid ${theme.colors.greyLight}`),
                                    minWidth: { md: 230 },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: '"Rubik", sans-serif',
                                        fontWeight: 900,
                                        color: pick("#111", theme.colors.text.primary),
                                        fontSize: { xs: "1.15rem", sm: "1.25rem", md: "1.35rem" },
                                        lineHeight: 1.1,
                                    }}
                                >
                                    {d.percent}
                                </Typography>

                                <Typography
                                    sx={{
                                        mt: 0.75,
                                        fontFamily: '"Rubik", sans-serif',
                                        color: pick("rgba(0,0,0,0.70)", theme.colors.text.secondary),
                                        fontWeight: 500,
                                        fontSize: { xs: "0.85rem", sm: "0.9rem" },
                                        lineHeight: 1.2,
                                    }}
                                >
                                    En tu compra mayor a
                                </Typography>

                                <Typography
                                    sx={{
                                        mt: 0.35,
                                        fontFamily: '"Rubik", sans-serif',
                                        fontWeight: 800,
                                        color: pick("#111", theme.colors.text.primary),
                                        fontSize: { xs: "0.95rem", sm: "1.0rem" },
                                    }}
                                >
                                    {d.threshold}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>

                    <Divider
                        sx={{
                            my: { xs: 2.25, sm: 2.75, md: 3.25 },
                            borderColor: pick("rgba(0,0,0,0.12)", theme.colors.greyLight),
                        }}
                    />

                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={{ xs: 1, sm: 2.5 }}
                        justifyContent="center"
                        alignItems="center"
                        sx={{ mt: 0.25 }}
                    >
                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 1,
                                px: 1.75,
                                py: 0.9,
                                borderRadius: 999,
                                bgcolor: pick("rgba(0,0,0,0.05)", theme.colors.greyExtraDark),
                                border: pick("1px solid rgba(0,0,0,0.06)", `1px solid ${theme.colors.greyLight}`),
                            }}
                        >
                            <ShoppingCartOutlinedIcon sx={{ fontSize: 20, color: pick("#111", theme.colors.text.primary) }} />
                            <Typography
                                sx={{
                                    fontFamily: '"Rubik", sans-serif',
                                    fontWeight: 700,
                                    color: pick("#111", theme.colors.text.primary),
                                    fontSize: { xs: "0.95rem", sm: "1.0rem" },
                                }}
                            >
                                {slide.ctaText}
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 1,
                                px: 1.75,
                                py: 0.9,
                                borderRadius: 999,
                                bgcolor: pick("rgba(0,0,0,0.05)", theme.colors.greyExtraDark),
                                border: pick("1px solid rgba(0,0,0,0.06)", `1px solid ${theme.colors.greyLight}`),
                            }}
                        >
                            <LocationOnOutlinedIcon sx={{ fontSize: 20, color: pick("#111", theme.colors.text.primary) }} />
                            <Typography
                                sx={{
                                    fontFamily: '"Rubik", sans-serif',
                                    fontWeight: 600,
                                    color: pick("#111", theme.colors.text.primary),
                                    fontSize: { xs: "0.95rem", sm: "1.0rem" },
                                }}
                            >
                                {slide.locationText}
                            </Typography>
                        </Box>
                    </Stack>
                </Box>
            </Box>
        );
    };

    const ImageSlideItem: React.FC<{ slide: ImageSlide }> = ({ slide }) => {
        const [isTouched, setIsTouched] = useState(false);

        const onTouchStart = () => setIsTouched(true);
        const onTouchEnd = () => setIsTouched(false);
        const onTouchCancel = () => setIsTouched(false);

        return (
            <Link href={slide.link} underline="none" sx={{ textDecoration: "none", display: "block", height: "100%" }}>
                <Box
                    onTouchStart={onTouchStart}
                    onTouchEnd={onTouchEnd}
                    onTouchCancel={onTouchCancel}
                    sx={{
                        position: "relative",
                        cursor: "pointer",
                        overflow: "hidden",
                        height: "100%",
                        "&:hover .slide-overlay": {
                            opacity: 0.1,
                        },
                        "&:hover .hero-image": {
                            transform: "scale(1.05)",
                            filter: `brightness(${IMAGE_BRIGHTNESS.hover})`,
                        },
                        [`&:hover .slide-subtitle`]: {
                            opacity: 1,
                            transform: "translateY(-6px)",
                        },
                        "&:hover .subtitle-wrapper .slide-underline": {
                            opacity: 1,
                        },
                    }}
                >
                    <Box
                        component="img"
                        className="hero-image"
                        src={getImagePath(slide.image)}
                        alt={slide.title}
                        sx={{
                            ...imageStyles,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            transition: `transform 0.6s ease, filter 0.6s ease`,
                            transform: isTouched ? "scale(1.05)" : undefined,
                            filter: isTouched
                                ? `brightness(${IMAGE_BRIGHTNESS.hover})`
                                : `brightness(${IMAGE_BRIGHTNESS.normal})`,
                        }}
                    />

                    <Box
                        className="slide-overlay"
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            bgcolor: "black",
                            opacity: isTouched ? 0.1 : 0,
                            transition: `opacity ${HOVER_ANIMATION_DURATION}s ease-in-out`,
                            zIndex: 1,
                        }}
                    />

                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            zIndex: 2,
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            pointerEvents: "none",
                        }}
                    >
                        <Box
                            className="slide-subtitle"
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                flexDirection: "column",
                                transition: `opacity ${HOVER_ANIMATION_DURATION}s ease, transform ${HOVER_ANIMATION_DURATION}s ease`,
                                opacity: isTouched ? 1 : 0,
                                transform: isTouched ? "translateY(-6px)" : "translateY(0)",
                            }}
                        >
                            <Box
                                className="subtitle-wrapper"
                                sx={{
                                    ...subtitleWrapperStyles,
                                    pointerEvents: "auto",
                                    textAlign: "center",
                                    color: "white",
                                    display: "inline-block",
                                    whiteSpace: "nowrap",
                                    paddingBottom: "6px",
                                    position: "relative",
                                }}
                            >
                                <Box
                                    component="span"
                                    sx={{
                                        ...subtitleTextStyles,
                                        fontSize: "1.1rem",
                                        whiteSpace: "nowrap",
                                        display: "inline-block",
                                    }}
                                >
                                    {TEXTS.miraMas}
                                </Box>
                                <Box
                                    className="slide-underline"
                                    sx={{
                                        position: "absolute",
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        height: 3,
                                        backgroundColor: "white",
                                        opacity: isTouched ? 1 : 0,
                                        transition: `opacity ${HOVER_ANIMATION_DURATION}s ease-in-out`,
                                        zIndex: 3,
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Link>
        );
    };

    const SlideItem: React.FC<{ slide: CarouselSlide }> = ({ slide }) => {
        if (slide.type === "banner") {
            return <BannerSlideItem slide={slide} />;
        }
        return <ImageSlideItem slide={slide} />;
    };

    const CAROUSEL_HEIGHT = { xs: 640, sm: 620, md: 720, lg: 720 } as const;

    return (
        <Box
            sx={{
                position: "relative",
                height: CAROUSEL_HEIGHT,


                "& .swiper, & .swiper-wrapper, & .swiper-slide": {
                    height: "100%",
                },
            }}
        >
            <Swiper
                style={{ height: "100%" }}
                navigation={{
                    nextEl: ".swiper-button-next-custom",
                    prevEl: ".swiper-button-prev-custom",
                }}
                pagination={{
                    clickable: true,
                    el: ".swiper-pagination-custom",
                    bulletClass: "swiper-pagination-bullet-custom",
                    bulletActiveClass: "swiper-pagination-bullet-active-custom",
                }}
                modules={[Navigation, Autoplay, Pagination]}
                loop
                autoplay={{ delay: 5000 }}
            >
                {slides.map((slide, i) => (
                    <SwiperSlide
                        key={i}
                        data-swiper-autoplay={slide.autoplayDelayMs ? String(slide.autoplayDelayMs) : undefined}
                    >
                        <SlideItem slide={slide} />
                    </SwiperSlide>
                ))}
            </Swiper>

            <Box
                className="swiper-button-prev-custom"
                sx={{
                    position: "absolute",
                    left: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 10,
                    width: 30,
                    height: 30,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "black",
                    "&::after": { content: '"‹"', fontSize: 34 },
                }}
            />
            <Box
                className="swiper-button-next-custom"
                sx={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 10,
                    width: 30,
                    height: 30,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "black",
                    "&::after": { content: '"›"', fontSize: 34 },
                }}
            />

            <Box
                className="swiper-pagination-custom"
                sx={{
                    position: "absolute",
                    bottom: "15px",
                    left: 0,
                    right: 0,
                    display: "flex",
                    justifyContent: "center",
                    zIndex: 10,
                    gap: "6px",
                    "& .swiper-pagination-bullet-custom": {
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        backgroundColor: "rgba(0,0,0,0.3)",
                        opacity: 1,
                        transition: "background-color 0.3s ease-in-out",
                    },
                    "& .swiper-pagination-bullet-active-custom": {
                        backgroundColor: "white",
                        border: "1px solid rgba(0,0,0,0.25)",
                    },
                }}
            />
        </Box>
    );
}














